/**
 * Snapshot undo/redo for the annotation canvas.
 *
 * The editor previously stored live Fabric object references per action, which
 * could not represent a pixel erase at all: erasing mutates `object.clipPath`
 * into a ClippingGroup rather than adding or removing an object. Snapshots of
 * the whole canvas represent every mutation uniformly, so one completed user
 * action is always exactly one history step.
 *
 * Pure functions over caller-held state, matching `canvasLifecycle.ts`. The
 * component owns the state object so Vue 2 can observe it directly.
 */

export const SNAPSHOT_LIMIT = 50;

/**
 * Properties Fabric's own `toObject` does not emit, so they have to be named or
 * they are silently lost on the first undo.
 *
 * `erasable` is contributed by `@erase2d/fabric` rather than native Fabric;
 * without it every object stops being erasable after a restore.
 *
 * `objectCaching` is native but not serialized, and it defaults to true. The
 * shapes and arrow groups are created with it off, so without this a restored
 * shape renders from a cache while an identical never-undone one renders live.
 */
export const SNAPSHOT_PROPERTIES: string[] = ['erasable', 'objectCaching'];

export interface AnnotationHistoryState {
	/**
	 * Serialized as strings rather than object trees. Vue 2 would deep-observe
	 * an object tree this large on every push; strings are leaves.
	 */
	snapshots: string[];
	index: number;
	restoring: boolean;
}

/**
 * The slice of the Fabric canvas this module needs.
 *
 * Declared structurally rather than as `Canvas` because the spec has to drive
 * it with a fake: jsdom returns null from `getContext('2d')` and `canvas` is
 * not a dependency, so a real Fabric `Canvas` cannot be constructed in the test
 * harness at all.
 */
export interface SnapshotCanvas {
	toObject(propertiesToInclude?: string[]): unknown;
	loadFromJSON(json: string): Promise<unknown>;
}

export const createAnnotationHistoryState = (): AnnotationHistoryState => ({
	snapshots: [],
	index: -1,
	restoring: false,
});

const serialize = (canvas: SnapshotCanvas): string => {
	return JSON.stringify(canvas.toObject(SNAPSHOT_PROPERTIES));
};

/**
 * Index 0 is the empty-canvas baseline recorded at init, so there is always
 * something to undo back to and the check is a simple `> 0`.
 */
export const canUndo = (state: AnnotationHistoryState): boolean => {
	return !state.restoring && state.index > 0;
};

export const canRedo = (state: AnnotationHistoryState): boolean => {
	return !state.restoring && state.index < state.snapshots.length - 1;
};

/** Drops all history and records a fresh baseline for the current canvas. */
export const resetHistory = (state: AnnotationHistoryState, canvas: SnapshotCanvas): void => {
	state.snapshots = [serialize(canvas)];
	state.index = 0;
	state.restoring = false;
};

/**
 * Records one step. Returns whether anything was actually recorded, so callers
 * can tell a real mutation from a no-op.
 */
export const recordSnapshot = (state: AnnotationHistoryState, canvas: SnapshotCanvas): boolean => {
	// A restore is itself a canvas mutation. Guarding here rather than in the
	// component means no caller can forget to.
	if (state.restoring) return false;

	const next: string = serialize(canvas);

	// Suppress unchanged states. This is what makes overlapping recording
	// boundaries safe: a text edit that fires both `text:editing:exited` and
	// `object:modified` collapses to one step, and a no-op transform or an
	// empty eraser stroke records nothing.
	if (state.snapshots[state.index] === next) return false;

	// Anything ahead of the cursor is a branch the user abandoned by acting.
	const kept: string[] = state.snapshots.slice(0, state.index + 1);
	kept.push(next);

	// Snapshots grow after erasing, because clip paths add path data per erased
	// object, so the cap is a real memory bound rather than a formality.
	state.snapshots = kept.length > SNAPSHOT_LIMIT
		? kept.slice(kept.length - SNAPSHOT_LIMIT)
		: kept;
	state.index = state.snapshots.length - 1;

	return true;
};

/**
 * `restoring` is held across the whole load, not just the synchronous part, so
 * a held-down Cmd+Z cannot interleave two `loadFromJSON` calls. It is released
 * in `finally` so a rejected load cannot wedge the stack.
 */
const restoreTo = async (
	state: AnnotationHistoryState,
	canvas: SnapshotCanvas,
	targetIndex: number,
): Promise<boolean> => {
	state.restoring = true;

	try {
		await canvas.loadFromJSON(state.snapshots[targetIndex]);
		// Only advance once the load actually landed, so a rejection leaves the
		// cursor pointing at the state still on screen.
		state.index = targetIndex;
		return true;
	} finally {
		state.restoring = false;
	}
};

export const undoSnapshot = async (
	state: AnnotationHistoryState,
	canvas: SnapshotCanvas,
): Promise<boolean> => {
	if (!canUndo(state)) return false;
	return await restoreTo(state, canvas, state.index - 1);
};

export const redoSnapshot = async (
	state: AnnotationHistoryState,
	canvas: SnapshotCanvas,
): Promise<boolean> => {
	if (!canRedo(state)) return false;
	return await restoreTo(state, canvas, state.index + 1);
};
